import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { EApiMiddlewareMethods } from "../../network/networkHandler.types";
import { 
  ConsignmentDetailsState, 
  ConsignmentDetails,
  FetchConsignmentDetailsParams
} from "./ConsignmentDetailsSlice.type";
import { ConsignmentDetailsMapper } from "../ConsignmentDetailsMapper";
import customNetworHandler from "../../network/customNetworHandler";
import { cWingsServiceBaseURL } from "../../config/Api.config";

// Async thunk for fetching consignment details by ID
export const fetchConsignmentDetails = createAsyncThunk<
  ConsignmentDetails,
  FetchConsignmentDetailsParams,
  { rejectValue: { error: string; message: string } }
>(
  "consignmentDetails/FetchConsignmentDetails",
  async ({ consignmentId }, { rejectWithValue }) => {
    try {
      
      const queryParams = new URLSearchParams();
      queryParams.append('consignments', consignmentId);
      queryParams.append('fetchStatusHistory', 'true');
      queryParams.append('fetchRouteDetails', 'true');
      queryParams.append('fetchRoute', 'true');
      queryParams.append('fetchProducts', 'true');

      const request = {
        url: `/fulfilment/order-details/consignment?${queryParams.toString()}`,
        method: EApiMiddlewareMethods.GET
      };

      const { data } = await customNetworHandler(request, cWingsServiceBaseURL);
      
      // API returns an array, get the first element
      const apiData = Array.isArray(data) && data.length > 0 ? data[0] : data;
      
      // Validate API response
      if (!apiData) {
        throw new Error('No consignment data found in API response');
      }
      
      // Use mapper to transform API response to UI format
      return ConsignmentDetailsMapper.mapApiResponseToConsignmentDetails(apiData);
    } catch (error: any) {
      console.error('Error fetching consignment details:', error);
      return rejectWithValue({
        error: 'FETCH_CONSIGNMENT_ERROR',
        message: error.message || 'Failed to fetch consignment details'
      });
    }
  }
);


// Initial state with proper types
const initialState: ConsignmentDetailsState = {
  loading: false,
  errorCode: "",
  error: "",
  // Consignment details data
  consignmentDetails: null,
  consignmentDetailsLoading: false,
  consignmentDetailsError: "",
};

// Slice definition
const ConsignmentDetailsSlice = createSlice({
  name: "consignmentDetails",
  initialState,
  reducers: {
    clearConsignmentDetails: (state) => {
      state.consignmentDetails = null;
      state.consignmentDetailsError = "";
    }
  },
  extraReducers: (builders) => {
    builders
      // Fetch single consignment details
      .addCase(fetchConsignmentDetails.pending, (state) => {
        state.consignmentDetailsLoading = true;
        state.consignmentDetailsError = "";
      })
      .addCase(fetchConsignmentDetails.fulfilled, (state, action) => {
        state.consignmentDetailsLoading = false;
        state.consignmentDetails = action.payload;
      })
      .addCase(fetchConsignmentDetails.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.consignmentDetailsLoading = false;
        state.consignmentDetailsError = errorPayload
          ? errorPayload.message || "Failed to fetch consignment details"
          : "Failed to fetch consignment details";
      });
  },
});

export const { 
  clearConsignmentDetails
} = ConsignmentDetailsSlice.actions;

export default ConsignmentDetailsSlice.reducer;
